import React, { useEffect } from 'react';
import { StyleSheet, View } from 'react-native';
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withTiming,
} from 'react-native-reanimated';
import Svg, { Path } from 'react-native-svg';

import { theme } from '@garnish/constants';
import { BodyText, LoadingPlaceholder } from '@garnish/core';

import { type ScanToPayPalette, textPalettes } from '../../utils';

export const ScanToPayLoadingState = (props: ScanToPayLoadingStateProps) => {
  const { title, isLoadingSize, hexagonSize, palette } = props;

  return (
    <View style={styles.container}>
      <BodyText sizeMatch={['22']} style={[styles.title, textPalettes[palette]]}>
        {title}
      </BodyText>

      {isLoadingSize ? (
        <View style={styles.wrapper} />
      ) : (
        <LoadingAnimation hexagonSize={hexagonSize} palette={palette} />
      )}

      <LoadingPlaceholder
        palette={palette === 'cream' ? 'darkGray' : 'darkestGray'}
        rows={1}
        columns={1}
        style={styles.boxPlaceholder}
        rowHeight={150}
      />

      <View style={styles.footer}>
        <View style={styles.footerItem}>
          <LoadingPlaceholder
            palette={palette === 'cream' ? 'darkGray' : 'darkestGray'}
            rows={1}
            columns={1}
            borderRadius={48}
            rowHeight={48}
          />
        </View>

        <View style={styles.footerItem}>
          <LoadingPlaceholder
            palette={palette === 'cream' ? 'darkGray' : 'darkestGray'}
            rows={1}
            columns={1}
            borderRadius={48}
            rowHeight={48}
          />
        </View>
      </View>
    </View>
  );
};

// ─── Sub Components ──────────────────────────────────────────────────────────

const LoadingAnimation = (props: Pick<ScanToPayLoadingStateProps, 'hexagonSize' | 'palette'>) => {
  const { hexagonSize, palette } = props;

  const fillOpacity = palette === 'cream' ? 0.05 : 0.5;
  const fill = palette === 'cream' ? theme.colors.DARK_KALE : theme.colors.WHITE;

  // ────────────────────────────────────────────────────────────────────────────────

  const opacity = useSharedValue(0);

  const animatedStyle = useAnimatedStyle(() => {
    return {
      opacity: opacity.value === 0 ? 1 : opacity.value === 1 ? 1 : 0,
    };
  });

  useEffect(() => {
    opacity.value = withRepeat(
      withTiming(1, {
        duration: 2250,
        easing: Easing.inOut(Easing.sin),
      }),
      -1,
      false,
    );
  }, [opacity]);

  // ────────────────────────────────────────────────────────────────────────────────

  return (
    <View style={styles.wrapper}>
      <Animated.View style={animatedStyle}>
        <Svg width={hexagonSize} height={hexagonSize} viewBox="0 0 215 196" fill="none">
          <Path
            d="M43.5681 14.769C48.5337 6.09986 57.7276 0.76937 67.6867 0.78545L147.001 0.913513C156.96 0.929592 166.172 6.28981 171.165 14.975L210.933 84.1448C215.927 92.83 215.944 103.521 210.978 112.19L171.432 181.232C166.466 189.901 157.272 195.231 147.313 195.215L67.9985 195.087C58.0395 195.071 48.8284 189.711 43.8349 181.026L4.06665 111.856C-0.926789 103.171 -0.94395 92.4798 4.02164 83.8107L43.5681 14.769Z"
            fill={fill}
            fillOpacity={fillOpacity}
            fillRule="evenodd"
            clipRule="evenodd"
          />
        </Svg>
      </Animated.View>
    </View>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flexGrow: 1,
    alignItems: 'center',
    paddingTop: theme.spacing['6'],
    gap: theme.spacing['10'],
  },
  wrapper: {
    flex: 1,
    alignItems: 'center',
    textAlign: 'center',
  },
  boxPlaceholder: {
    width: '100%',
  },
  title: {
    textAlign: 'center',
    paddingHorizontal: theme.spacing['4'],
    color: theme.colors.KALE,
  },
  footer: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 'auto',
    gap: theme.spacing['4'],
    paddingHorizontal: theme.spacing['4'],
  },
  footerItem: {
    flex: 1,
    paddingBottom: theme.spacing['6'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type ScanToPayLoadingStateProps = {
  title: string;
  hexagonSize: number;
  isLoadingSize: boolean;
  palette: ScanToPayPalette;
};
